/**
 * Consulta pública do PNCP (Portal Nacional de Contratações Públicas) para preencher uma licitação (D70).
 * A API de consulta e a de itens liberam acesso pelo navegador; os arquivos são baixados pela Edge Function pncp-arquivo.
 */
import type { ControlePncp } from "@sobral/dominio";

export interface CompraPncp {
  numeroControlePNCP: string;
  numeroCompra: string;
  anoCompra: number;
  processo: string | null;
  objetoCompra: string;
  srp: boolean;
  modalidadeId: number;
  modalidadeNome: string;
  dataAberturaProposta: string | null;
  dataEncerramentoProposta: string | null;
  linkSistemaOrigem: string | null;
  orgaoEntidade: { cnpj: string; razaoSocial: string; esferaId: string };
  unidadeOrgao: { ufSigla: string; municipioNome: string; codigoUnidade: string; nomeUnidade: string };
  situacaoCompraNome: string;
}

export interface ItemPncp {
  numeroItem: number;
  descricao: string;
  quantidade: number;
  unidadeMedida: string | null;
  valorUnitarioEstimado: number | null;
  orcamentoSigiloso: boolean;
  tipoBeneficio: number | null;
  ncmNbsCodigo: string | null;
  situacaoCompraItemNome: string | null;
  informacaoComplementar: string | null;
}

export interface ArquivoPncp {
  url: string;
  titulo: string;
  tipoDocumentoNome: string;
  statusAtivo: boolean;
}

const BASE = "https://pncp.gov.br/api";

async function obter<T>(caminho: string): Promise<T> {
  let resposta: Response;
  try {
    resposta = await fetch(`${BASE}${caminho}`, { signal: AbortSignal.timeout(20000) });
  } catch {
    throw new Error("O PNCP não respondeu. Tente de novo em instantes.");
  }
  if (resposta.status === 404) throw new Error("Licitação não encontrada no PNCP. Confira o número ou o link.");
  if (!resposta.ok) throw new Error(`O PNCP recusou a consulta (HTTP ${resposta.status}).`);
  return (await resposta.json()) as T;
}

const caminhoCompra = (c: ControlePncp) => `/orgaos/${c.cnpj}/compras/${c.ano}/${c.sequencial}`;

export const consultarCompra = (c: ControlePncp) => obter<CompraPncp>(`/consulta/v1${caminhoCompra(c)}`);

/** Todos os itens da compra (a API pagina de 500 em 500). */
export async function consultarItens(c: ControlePncp): Promise<ItemPncp[]> {
  const itens: ItemPncp[] = [];
  for (let pagina = 1; pagina <= 20; pagina++) {
    const lote = await obter<ItemPncp[]>(`/pncp/v1${caminhoCompra(c)}/itens?pagina=${pagina}&tamanhoPagina=500`);
    itens.push(...lote);
    if (lote.length < 500) break;
  }
  return itens;
}

export async function consultarArquivos(c: ControlePncp): Promise<ArquivoPncp[]> {
  const arquivos = await obter<ArquivoPncp[]>(`/pncp/v1${caminhoCompra(c)}/arquivos`);
  return arquivos.filter((a) => a.statusAtivo);
}

/**
 * Data e hora do PNCP ("2025-09-25T14:00:00", horário de Brasília, sem fuso) → ISO com -03:00.
 * Brasília está em UTC-3 desde o fim do horário de verão (Decreto 9.772/2019).
 */
export function dataHoraPncp(valor: string | null | undefined): string | null {
  if (!valor) return null;
  const m = valor.match(/^(\d{4}-\d{2}-\d{2}T\d{2}:\d{2})(:\d{2})?/);
  return m ? `${m[1]}${m[2] ?? ":00"}-03:00` : null;
}

/** Tipo de arquivo da licitação pelo nome do documento no PNCP ou pelo nome do arquivo. */
export function tipoDeArquivo(nome: string): "edital" | "termo_referencia" | "anexo" | "proposta" | "ata" | "outro" {
  const t = nome
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase();
  if (/termo de referencia|\btr\b/.test(t)) return "termo_referencia";
  if (/\bedital\b/.test(t)) return "edital";
  if (/\bata\b/.test(t)) return "ata";
  if (/proposta/.test(t)) return "proposta";
  if (/anexo|planilha|modelo|estudo|etp/.test(t)) return "anexo";
  return "outro";
}

export const ESFERA_PNCP: Record<string, "federal" | "estadual" | "municipal"> = { F: "federal", E: "estadual", M: "municipal" };

/** Endereço da página do edital no PNCP a partir do número de controle ("CNPJ-1-SEQUENCIAL/ANO"). */
export function linkPncp(numeroControle: string): string | null {
  const m = numeroControle.match(/^(\d{14})-\d-(\d{1,6})\/(\d{4})$/);
  return m ? `https://pncp.gov.br/app/editais/${m[1]}/${m[3]}/${Number(m[2])}` : null;
}

/** Item ou compra com situação diferente de "Em andamento" no PNCP (anulado, deserto, fracassado, homologado…). */
export function situacaoFoge(situacao: string | null | undefined): boolean {
  return Boolean(situacao && situacao.trim().toLowerCase() !== "em andamento");
}
